import cx from "classnames";
import { DateTime } from "luxon";
import { ChooseFpModal } from "modules/finances/entities/month-plan/choose-fp-modal";
import { Percent } from "modules/finances/_shared";
import Link from "next/link";
import pages from "pages/_router";
import React, { CSSProperties, useMemo, useState } from "react";
import { FinancialPlanSmallDto, MonthPlanSimpleDto } from "shared/api";
import { capitalize, formatDate, formatNumber, formatPercent, Modals, pluralize } from "shared/lib";
import { Badge, Entity, EntityForm, Icons, StatusDot, Tooltip } from "shared/ui";
import styles from "./month-plan-entity.module.scss";

export interface IMonthPlanEntity {
  currentFP?: FinancialPlanSmallDto | null;
  data: MonthPlanSimpleDto;
  companySlug: string;
  initialOpen?: boolean;

  className?: string;
  style?: CSSProperties;
  htmlId?: string;
}

export const MonthPlanEntity = (props: IMonthPlanEntity) => {
  const data = props.data;
  const date = DateTime.local(props.data.year, props.data.month);

  const hasExpiredWeeks = !!props.data.weeks.find((it) => !it.closed && it.to < new Date());
  const route = pages.company(props.companySlug).finances.yearMonth(props.data.year, props.data.month);
  const draft =
    props.currentFP?.monthPlanId === props.data.id && props.currentFP?.status !== "APPROVED"
      ? props.currentFP
      : undefined;

  const weeks = useMemo(() => {
    const now = new Date();

    return data.weeks.map((it, index) => {
      if (data.status === "DRAFT" || data.status === "ON_APPROVAL") return { ...it, status: "none" };

      const planningNow = index === draft?.monthWeekPosition;
      const expired = !it.closed && it.to < now;
      const currentWeek = it.from < now && now < it.to;

      let status: "expired" | "current" | "planning" | "none" = "none";

      if (currentWeek) {
        status = "current";
      }
      if (expired) {
        status = "expired";
      }
      if (planningNow) {
        status = "planning";
      }

      return { ...it, status };
    });
  }, [data, draft]);

  const status = useMemo(() => {
    if (data.status !== "APPROVED" && data.status !== "APPROVED_AND_HAS_FINANCIAL_PLAN") return "none";

    let hasExpired = false;
    let hasPlanning = false;
    let hasCurrent = false;

    weeks.forEach((it) => {
      if (it.status === "expired") hasExpired = true;
      if (it.status === "planning") hasPlanning = true;
      if (it.status === "current") hasCurrent = true;
    });

    if (hasExpired) return "expired";
    if (hasPlanning) return "planning";
    if (hasCurrent) return "current";
    return "none";
  }, [weeks, data.status]);

  const [open, setOpen] = useState(props.initialOpen);

  return (
    <>
      <Entity
        className={cx(styles.month_plan_entity, open && styles.open, props.className)}
        style={props.style}
        htmlId={props.htmlId}
        hover
      >
        <div
          className={styles.opener}
          onClick={(e) => {
            e.stopPropagation();
            setOpen((it) => !it);
          }}
        >
          <Icons.Down />
        </div>

        <Entity.Field
          title={
            <Link href={route.href} className={styles.link}>
              {capitalize(date.setLocale("ru").toLocaleString({ month: "long" }))}
              {status === "planning" && (
                <StatusDot status="blue" className={cx(open && styles.transparent)} style={{ marginLeft: 8 }} />
              )}
              {status === "expired" && (
                <StatusDot status="red" className={cx(open && styles.transparent)} style={{ marginLeft: 8 }} />
              )}
              {status === "current" && (
                <StatusDot status="white" className={cx(open && styles.transparent)} style={{ marginLeft: 8 }} />
              )}
            </Link>
          }
          description={
            <span className={styles.primary}>
              {formatDate(data.from)} — {formatDate(data.to)}
            </span>
          }
          width={130}
        />

        <div className={styles.group}>
          <Entity.Field
            title="План дохода"
            description={<span className={styles.plan}>{formatNumber(data.planIncome || "—")}</span>}
            width={95}
            label
            right
          />
          <Entity.Field title="Доход" description={formatNumber(data.factIncome || "—")} width={85} label right />
          <Entity.Field
            title="%"
            description={data.factIncome ? formatPercent(data.factIncome / data.planIncome) : ""}
            width={45}
            label
            right
            className={styles.percent}
          />
        </div>

        {/*<Entity.Field title="" description="" width={45} label right />*/}

        <div className={styles.group}>
          <Entity.Field
            title="План маржи"
            description={<span className={styles.plan}>{formatNumber(data.planMarginProfit || "—")}</span>}
            width={95}
            label
            right
          />
          <Entity.Field title="Маржа" description={formatNumber(data.factMarginProfit || "—")} width={85} label right />
          <Entity.Field
            title="%"
            description={data.factMarginProfit ? formatPercent(data.factMarginProfit / data.planMarginProfit) : ""}
            width={45}
            label
            right
            className={styles.percent}
          />
        </div>
        {/*<Entity.Field title="" description="" width={45} label right />*/}

        <Entity.Actions className={styles.actions}>
          {data.status === "DRAFT" && "черновик"}
          {data.status === "ON_APPROVAL" && "на утверждении"}
        </Entity.Actions>
      </Entity>

      {open && (
        <EntityForm className={styles.footer}>
          {weeks.map((it, weekNumber) => {
            const showFact = it.financialPlans.length !== 0;

            const noFPs = it.financialPlans.length === 0;
            const oneFP = it.financialPlans.length === 1;
            const fewFPs = it.financialPlans.length >= 2;

            const Component = oneFP ? Link : "div";

            return (
              <Component
                href={route.results.id(it.financialPlans[0]?.id!).href}
                className={cx(styles.week, !noFPs && styles.hover, it.closed && styles.closed)}
                key={weekNumber}
                onClick={() => {
                  if (!fewFPs) return;

                  Modals.open(ChooseFpModal, { data: it, route });
                }}
              >
                {it.status === "expired" && (
                  <span className={styles.status} title="Пропущено финансовое планирование">
                    <StatusDot status="red" />
                  </span>
                )}
                {it.status === "current" && !hasExpiredWeeks && (
                  <span className={styles.status} title="Текущая неделя">
                    <StatusDot status="white" />
                  </span>
                )}
                {it.status === "planning" && (
                  <span className={styles.status} title="Сейчас проводится планирование">
                    <StatusDot status="blue" />
                  </span>
                )}
                <span className={styles.date}>
                  {formatDate(it.from, { year: undefined, month: undefined })} —{" "}
                  {formatDate(it.to, { year: undefined })}
                  {fewFPs && (
                    <Tooltip
                      label={`За неделю провели ${pluralize(it.financialPlans.length, {
                        one: (it) => `${it}\u00A0финансовое планирование`,
                        few: (it) => `${it}\u00A0финансовых планирования`,
                        many: (it) => `${it}\u00A0финансовых планирований`,
                        value2: (it) => "два финансовых планирования",
                        value3: (it) => "три финансовых планирования",
                        value4: (it) => "четыре финансовых планирования",
                        value5: (it) => "пять финансовых планирования",
                        value6: (it) => "шесть финансовых планирования",
                        value7: (it) => "семь финансовых планирования",
                      })}`}
                    >
                      <div style={{ marginLeft: 8 }}>
                        <Badge variant="gray-subtle" size="sm">
                          {it.financialPlans.length}
                        </Badge>
                      </div>
                    </Tooltip>
                  )}
                </span>
                <span className={styles.group}>
                  <span className={cx(styles.income_plan, styles.plan)}>
                    {formatNumber(it.plan.incomeTotal || undefined) || "—"}
                  </span>

                  {showFact && (
                    <>
                      <span className={styles.income_fact}>{formatNumber(it.fact.incomeTotal)}</span>
                      <span className={cx(styles.income_percent)}>
                        <Percent colorized={it.closed}>{it.fact.incomeTotal / it.plan.incomeTotal}</Percent>
                      </span>
                    </>
                  )}
                </span>

                <span className={styles.group}>
                  <span className={cx(styles.margin_plan, styles.plan)}>
                    {formatNumber(it.plan.marginProfit || undefined) || "—"}
                  </span>
                  {showFact && (
                    <>
                      <span className={styles.margin_fact}>{formatNumber(it.fact.marginProfit)}</span>
                      <span className={styles.margin_percent}>
                        <Percent colorized={it.closed}>{it.fact.marginProfit / it.plan.marginProfit}</Percent>
                      </span>
                    </>
                  )}
                </span>
              </Component>
            );
          })}
        </EntityForm>
      )}
    </>
  );
};

MonthPlanEntity.defaultProps = {
  initialOpen: false,
};
